import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin } from "lucide-react";
import { TextInput, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { imgContactLady } from "../../assets";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { ButtonLoader } from "../../components/Loader";

interface SupportRequest {
  first_name: string;
  last_name: string;
  email: string;
  message: string;
}

export default function Contact() {
  const MotionButton = motion.button;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      fullName: "",
      email: "",
      message: "",
    },
    validate: {
      fullName: (value: string) => {
        if (!value.trim()) return "Full name is required";
        const parts = value.trim().split(" ");
        if (parts.length < 2) return "Please enter your first and last name";
        return null;
      },
      email: (value: string) => {
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Please enter a valid email address";
        }
        return null;
      },
      message: (value: string) => {
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10) {
          return "Message must be at least 10 characters";
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      const nameParts = values.fullName.trim().split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(" ") || "";

      const endpoint = Endpoints.ContactSupport();
      const response = await PerformRequest<{ message: string }>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          first_name: firstName,
          last_name: lastName,
          email: values.email,
          message: values.message,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message:
            "Your message has been sent successfully! We'll get back to you soon.",
          color: "green",
        });
        form.reset();
      } else {
        notifications.show({
          title: "Error",
          message:
            response.message || "Failed to send message. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.message || "Failed to send message. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact" data-node-id="249:134">
      <section className="contact__main" data-node-id="250:213">
        <div className="container contact__main-inner">
          <div
            className="contact-card rounded-2xl shadow-[var(--card-shadow)]"
            data-node-id="250:214"
          >
            <form
              className="contact-form space-y-4"
              onSubmit={form.onSubmit(handleSubmit)}
            >
              <TextInput
                label="Full Name"
                placeholder="Enter your full name"
                autoComplete="name"
                disabled={isSubmitting}
                error={form.errors.fullName}
                {...form.getInputProps("fullName")}
              />

              <TextInput
                label="Email Address"
                placeholder="Enter your email"
                type="email"
                autoComplete="email"
                disabled={isSubmitting}
                error={form.errors.email}
                {...form.getInputProps("email")}
              />

              <Textarea
                label="Message"
                placeholder="Enter your message"
                rows={4}
                disabled={isSubmitting}
                error={form.errors.message}
                {...form.getInputProps("message")}
              />

              <MotionButton
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="contact-form__button transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2 flex items-center justify-center gap-2"
                type="submit"
                disabled={isSubmitting}
                data-node-id="250:226"
              >
                {isSubmitting ? (
                  <>
                    <ButtonLoader color="white" />
                    <span>Sending...</span>
                  </>
                ) : (
                  "Send Message"
                )}
              </MotionButton>

              <div className="contact-meta" data-node-id="250:249">
                <div className="contact-meta__row">
                  <MapPin size={20} aria-hidden="true" />
                  <div>
                    <div>The Zone, 9 Gbagada-owonroshoki Expy,</div>
                    <div>Lagos State.</div>
                  </div>
                </div>
                <div className="contact-meta__row" data-node-id="250:248">
                  <Mail size={20} aria-hidden="true" />
                  <div>info@nbaremunerationportal.org</div>
                </div>
              </div>
            </form>
          </div>

          <div className="contact-image" data-node-id="250:253">
            <img
              className="contact-image__img rounded-2xl shadow-[var(--card-shadow)]"
              src={imgContactLady}
              alt=""
            />
          </div>
        </div>
      </section>
    </div>
  );
}
